import type { PropsOf } from '@emotion/react';
import styled from '@emotion/styled';
import { elevation, radius, spacer } from '@tokens';
import { Text } from '@components/Text/Text';
import { BORDER_COLORS, BACKGROUND_COLORS } from '../helpers';
import type { ToastKind } from '../helpers';
import { NotificationIcon } from '../NotificationIcon';

export interface ToastProps extends PropsOf<'div'> {
  /**
   * Text for the toast.
   */
  children: string;
  /**
   * Kind of toast.
   */
  kind: ToastKind;
  /**
   * Styles override.
   */
  className?: string;
}

const TOAST_MAX_WIDTH = '300px';

const ToastContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'kind',
})<{ kind: ToastKind }>`
  max-width: ${TOAST_MAX_WIDTH};
  background-color: ${({ kind }) => BACKGROUND_COLORS[kind]};
  border: 1px solid ${({ kind }) => BORDER_COLORS[kind]};
  border-radius: ${radius.l};
  padding: ${spacer.x2} ${spacer.x3};
  display: flex;
  align-items: flex-start;
  ${elevation(2)};
  gap: ${spacer.x2};
`;

const StyledIcon = styled(NotificationIcon)`
  // specific margin to make sure the first line is well centered with the icon
  margin-top: 2px;
`;

const StyledText = styled(Text)`
  overflow: hidden;
  overflow-wrap: break-word;
  margin-bottom: 0;
`;

/**
 * Auto-dismissing alert displayed at the bottom of the screen.
 *
 * {@link https://zeroheight.com/76c439891/p/06187d-toast/b/480021 Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-toast--basic-toast Code documentation}
 *
 * @example
 * const { showToast } = useNotify();
 * const onPress = () => {
 *   showToast({ text: 'This is a Toast', 'info' });
 * };
 */
export const Toast = ({
  children,
  kind,
  ...props
}: ToastProps): JSX.Element => {
  return (
    <ToastContainer
      {...props}
      kind={kind}
      data-testid={`toast-${kind}`}
      role="status"
    >
      <StyledIcon kind={kind} />
      <StyledText as="p">{children}</StyledText>
    </ToastContainer>
  );
};
